/**
 * useConversationChat Hook
 * API hooks for conversation chat operations with streaming support
 *
 * Refactored: Phase 4 - Factory Pattern Migration
 * Pattern: Uses createMutationHook factory for standardized mutations
 * Note: useSendMessage uses custom implementation for SSE streaming (cannot use factory)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiFetchUrl } from '../../config/apiConfig';
import { conversationKeys } from './useConversations';
import { log } from '../../utils/errors/logger';
import { ChatRequest } from '../../api/data-contracts';
import { getCsrfToken, requiresCsrfToken } from '../../utils/auth/csrfManager';

// --- Types ---

interface SendMessageVariables {
  conversationId: string;
  content: string;
  onChunk?: (chunk: string, context?: Record<string, unknown>[]) => void;
}

interface SendMessageResponse {
  message: { role: 'assistant'; content: string };
  context: Record<string, unknown>[];
  id: string;
  created: number;
}

// --- Custom Streaming Hook (Cannot use factory due to SSE) ---

/**
 * Hook to send a message in a conversation with streaming support
 *
 * Note: This hook uses custom implementation because it handles Server-Sent Events (SSE)
 * streaming which requires direct fetch API access and custom response handling.
 * The factory pattern is not suitable for this use case.
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ conversationId, content, onChunk }: SendMessageVariables): Promise<SendMessageResponse> => {      // Headers for streaming request
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // SECURITY: Add CSRF token for POST request (VULN-001 FIX)
      const csrfToken = getCsrfToken();
      if (csrfToken && requiresCsrfToken('POST')) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const chatRequest: ChatRequest = {
        messages: [{ role: 'user', content }],
        stream: true, // Enable streaming
      };

      log.api.request('POST', `/api/conversations/${conversationId}/chat`, {
        conversationId,
        content,
        streaming: true
      });

      const response = await fetch(`${getApiFetchUrl()}/conversations/${conversationId}/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(chatRequest),
        credentials: 'include', // Send cookies for authentication
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle streaming response
      let fullContent = '';
      let context: Record<string, unknown>[] = [];

      if (response.body && onChunk) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                try {
                  const data = JSON.parse(line.slice(6)) as Record<string, unknown>;

                  if (data.type === 'context') {
                    context = (data.context as Record<string, unknown>[] | undefined) ?? [];
                    onChunk('', context);
                  } else if (data.type === 'content') {
                    fullContent += (data.content as string);
                    onChunk(data.content as string, context);
                  } else if (data.type === 'done') {
                    break;
                  } else if (data.type === 'error') {
                    // Check if it's a quota error (429)
                    const errorStatus = data.status as number | undefined;
                    const errorCode = data.code as string | undefined;
                    const errorMessage = (data.error as string | undefined) ?? 'Streaming error occurred';

                    if (errorStatus === 429 || errorCode === 'QUOTA_EXCEEDED') {
                      // Create a quota-specific error
                      const quotaError = new Error(errorMessage);
                      (quotaError as any).status = 429;
                      (quotaError as any).code = 'QUOTA_EXCEEDED';
                      throw quotaError;
                    }

                    throw new Error(errorMessage);
                  }
                } catch (parseError) {
                  log.warn('Failed to parse SSE data', {
                    line,
                    error: parseError instanceof Error ? parseError.message : String(parseError)
                  });
                }
              }
            }
          }
        } finally {
          reader.releaseLock();
        }
      } else {
        // Fallback to non-streaming if no onChunk callback
        const data = await response.json() as { message?: { content?: string }; context?: Record<string, unknown>[] };
        fullContent = data.message?.content ?? '';
        context = data.context ?? [];
      }

      log.api.response('POST', `/api/conversations/${conversationId}/chat`, response.status, {
        messageLength: fullContent.length,
        contextCount: context.length,
        streaming: Boolean(onChunk)
      });

      return {
        message: { role: 'assistant' as const, content: fullContent },
        context,
        id: Date.now().toString(),
        created: Math.floor(Date.now() / 1000)
      };
    },
    onMutate: async ({ conversationId, content }) => {
      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({
        queryKey: conversationKeys.detail(conversationId)
      });

      // Snapshot previous value for rollback
      const previousConversation = queryClient.getQueryData(
        conversationKeys.detail(conversationId)
      );

      // Optimistically update cache with temporary user message
      queryClient.setQueryData(
        conversationKeys.detail(conversationId),
        (old: any) => {
          if (!old) {return old;}

          const optimisticMessage = {
            id: `temp-${Date.now()}`, // Temporary ID
            role: 'user',
            content,
            sources: [],
            timestamp: new Date().toISOString(),
            message_index: old.messages?.length || 0
          };

          return {
            ...old,
            messages: [...(old.messages || []), optimisticMessage]
          };
        }
      );

      return { previousConversation };
    },
    onSuccess: (data, { conversationId }) => {
      // Invalidate conversation details to refresh messages
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.detail(conversationId)
      });

      // Also invalidate messages query
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.messages(conversationId)
      });

      // Update conversation list to reflect new message count and preview
      void queryClient.invalidateQueries({
        queryKey: conversationKeys.lists()
      });

      log.user.action('message_sent', 'useConversationChat', {
        conversationId,
        hasContext: (data.context?.length ?? 0) > 0
      });
    },
    onError: (error, { conversationId }, context) => {
      // Rollback optimistic update on error
      if (context?.previousConversation) {
        queryClient.setQueryData(
          conversationKeys.detail(conversationId),
          context.previousConversation
        );
      }
      log.api.error('POST', `/api/conversations/${conversationId}/chat`, error);
    },
  });
}